import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Card,
  Typography,
  Box,
  Skeleton,
  Chip,
  useTheme,
  alpha,
  Fade,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { getMyRecruitersAction } from "../../redux/action";
import { userSelector } from "../../user-redux/selector";
import axios from "axios";
import Key from "../../../clientVariables.json";
import dayjs from 'dayjs';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BarChartIcon from '@mui/icons-material/BarChart';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { BriefcaseIcon } from "lucide-react";

const Totalbarchart = () => {
  const { user } = useSelector(userSelector);
  const dispatch = useDispatch();
  const theme = useTheme();

  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('overall'); // 'daily', 'weekly', 'monthly', 'overall'

  // Enhanced icon wrapper component for rich visual effects
  const RichIcon = ({
    children,
    color = theme.palette.primary.main,
    size = 'medium',
    variant = 'gradient' // 'gradient', 'glow', 'shadow', 'minimal'
  }) => {
    const sizeMap = {
      small: { p: 0.75, fontSize: '18px', borderRadius: 1.5 },
      medium: { p: 1, fontSize: '20px', borderRadius: 2 },
      large: { p: 1.5, fontSize: '24px', borderRadius: 2.5 }
    };

    const currentSize = sizeMap[size];

    const variantStyles = {
      gradient: {
        background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
        color: '#FFF',
        boxShadow: `0 4px 12px ${alpha(color, 0.3)}`,
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: `0 6px 16px ${alpha(color, 0.4)}`,
        }
      },
      glow: {
        backgroundColor: alpha(color, 0.15),
        color: color,
        boxShadow: `0 0 20px ${alpha(color, 0.2)}, inset 0 1px 0 ${alpha('#FFF', 0.2)}`,
        '&:hover': {
          backgroundColor: alpha(color, 0.25),
          boxShadow: `0 0 25px ${alpha(color, 0.3)}, inset 0 1px 0 ${alpha('#FFF', 0.3)}`,
        }
      },
      shadow: {
        backgroundColor: theme.palette.background.paper,
        color: color,
        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
        border: `1px solid ${alpha(color, 0.2)}`,
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.15)}`,
        }
      },
      minimal: {
        backgroundColor: alpha(color, 0.1),
        color: color,
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(color, 0.2)}`,
      }
    };

    return (
      <Box
        sx={{
          p: currentSize.p,
          borderRadius: currentSize.borderRadius,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          cursor: 'default',
          position: 'relative',
          overflow: 'hidden',
          '&::before': variant === 'gradient' ? {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '50%',
            background: `linear-gradient(180deg, ${alpha('#FFF', 0.2)} 0%, transparent 100%)`,
            pointerEvents: 'none',
          } : {},
          ...variantStyles[variant]
        }}
      >
        {React.cloneElement(children, {
          sx: { fontSize: currentSize.fontSize, ...children.props.sx }
        })}
      </Box>
    );
  };

  // Enhanced chip component with rich icons
  const RichChip = ({ icon, label, color, count }) => (
    <Chip
      icon={
        <RichIcon color={color} size="small" variant="glow">
          {icon}
        </RichIcon>
      }
      label={
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" fontWeight={600}>
            {count?.toLocaleString() || 0}
          </Typography>
          <Typography variant="body2">
            {label}
          </Typography>
        </Box>
      }
      variant="outlined"
      sx={{
        backgroundColor: alpha(color, 0.08),
        borderColor: alpha(color, 0.25),
        color: color,
        fontWeight: 600,
        height: 40,
        borderRadius: 3,
        '& .MuiChip-label': {
          px: 2,
        },
        '&:hover': {
          backgroundColor: alpha(color, 0.12),
          transform: 'translateY(-1px)',
          boxShadow: `0 4px 12px ${alpha(color, 0.2)}`,
        }
      }}
    />
  );

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            borderRadius: 3,
            padding: 2,
            boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.15)}`,
            backdropFilter: 'blur(10px)',
            minWidth: 200
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              marginBottom: 1,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
              paddingBottom: 0.5
            }}
          >
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Box key={index} sx={{ display: "flex", alignItems: "center", marginBottom: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: entry.color,
                  borderRadius: 1,
                  marginRight: 1,
                  boxShadow: `0 2px 8px ${alpha(entry.color, 0.3)}`
                }}
              />
              <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                {entry.name}: {entry.value.toLocaleString()}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  // Custom legend component
  const CustomLegend = ({ payload }) => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 3,
          marginTop: 2,
          flexWrap: "wrap"
        }}
      >
        {payload.map((entry, index) => (
          <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
            <Box
              sx={{
                width: 14,
                height: 14,
                backgroundColor: entry.color,
                borderRadius: 1,
                marginRight: 1,
                boxShadow: `0 2px 8px ${alpha(entry.color, 0.3)}`
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 600,
                fontSize: '0.875rem'
              }}
            >
              {entry.value}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  // Calculate summary statistics
  const summaryStats = React.useMemo(() => {
    if (!chartData.length) return { totalCandidates: 0,  totalJobs:0, totalRecruiters: 0, totalOnboard: 0 };
    

    const totalCandidates = chartData.reduce((sum, item) => sum + (item.totalCandidates || 0), 0);
    const totalJobs = chartData.reduce((sum, item) => sum + (item.totalJobs || 0), 0);
    const totalRecruiters = chartData.reduce((sum, item) => sum + (item.totalRecruiters || 0), 0);
    const totalOnboard = chartData.reduce((sum, item) => sum + (item.selectedCount || 0), 0);

    return { totalCandidates, totalJobs, totalRecruiters, totalOnboard };
  }, [chartData]);

  // Fetch dashboard analytics data when user or viewMode changes
  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    const fetchCounts = async () => {
      try {
        const now = dayjs();
        let fromDate = '';
        let payload = {
          user_id: user.id,
          interval: viewMode
        };
        if (viewMode === 'daily') {
          fromDate = now.format('YYYY-MM-DD');
          payload.fromDate = fromDate;
        } else if (viewMode === 'weekly') {
          fromDate = now.startOf('week').format('YYYY-MM-DD');
          payload.fromDate = fromDate;
        } else if (viewMode === 'monthly') {
          fromDate = now.startOf('month').format('YYYY-MM-DD');
          payload.fromDate = fromDate;
        }
        const response = await axios.post(
          `${Key.domain}/user/dashboard-analytics`,
          payload
        );
        const apiData = response?.data?.data?.[0];
        let chartDataResult = [];
        if (viewMode === 'daily') {
          chartDataResult = [{
            period: 'Today',
            totalCandidates: apiData?.candidates_count || 0,
            totalJobs: apiData?.jobs_count || 0,
            totalRecruiters: apiData?.recruiter_count || 0,
            selectedCount: apiData?.submissions_selected_count || 0,
          }];
        } else if (viewMode === 'weekly') {
          chartDataResult = [{
            period: 'This Week',
            totalCandidates: apiData?.candidates_count || 0,
            totalJobs: apiData?.jobs_count || 0,
            totalRecruiters: apiData?.recruiter_count || 0,
            selectedCount: apiData?.submissions_selected_count || 0,
          }];
        } else if (viewMode === 'monthly') {
          chartDataResult = [{
            period: 'This Month',
            totalCandidates: apiData?.candidates_count || 0,
            totalJobs: apiData?.jobs_count || 0,
            totalRecruiters: apiData?.recruiter_count || 0,
            selectedCount: apiData?.submissions_selected_count || 0,
          }];
        } else {
          chartDataResult = [{
            period: 'Overall',
            totalCandidates: apiData?.candidates_count || 0,
            totalJobs: apiData?.jobs_count || 0,
            totalRecruiters: apiData?.recruiter_count || 0,
            selectedCount: apiData?.submissions_selected_count || 0,
          }];
        }
        setChartData(chartDataResult);
      } catch (error) {
        console.error("Error fetching dashboard counts:", error);
        setError("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };
    fetchCounts();
  }, [user?.id, viewMode]);

  // Fetch recruiters only when user.companyid changes (not on filter change)
  useEffect(() => {
    if (user?.companyid) {
      const rec_data = {
        company_id: user?.companyid,
      };
      dispatch(getMyRecruitersAction(rec_data));
    }
  }, [dispatch, user?.companyid]);

  // ...existing code...

  const fetchCounts = async () => {
    if (!user?.id) {
      console.error("User ID is not available.");
      setError("User authentication required");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const now = dayjs();
      let fromDate = '';
      let chartDataResult = [];
      let payload = {
        user_id: user.id,
        interval: viewMode
      };

      if (viewMode === 'daily') {
        fromDate = now.format('YYYY-MM-DD');
        payload.fromDate = fromDate;

        const response = await axios.post(
          `${Key.domain}/user/dashboard-analytics`,
          payload
        );

        const apiData = response?.data?.data?.[0];
        chartDataResult = [{
          period: 'Today',
          totalCandidates: apiData?.candidates_count || 0,
          totalRecruiters: apiData?.recruiter_count || 0,
          selectedCount: apiData?.submissions_selected_count || 0,
        }];

      } else if (viewMode === 'weekly') {
        fromDate = now.startOf('week').format('YYYY-MM-DD');
        payload.fromDate = fromDate;

        const response = await axios.post(
          `${Key.domain}/user/dashboard-analytics`,
          payload
        );

        const apiData = response?.data?.data?.[0];
        chartDataResult = [{
          period: 'This Week',
          totalCandidates: apiData?.candidates_count || 0,
          totalRecruiters: apiData?.recruiter_count || 0,
          selectedCount: apiData?.submissions_selected_count || 0,
          totalJobs: apiData?.jobs_count || 0,
        }];

      } else if (viewMode === 'monthly') {
        fromDate = now.startOf('month').format('YYYY-MM-DD');
        payload.fromDate = fromDate;

        const response = await axios.post(
          `${Key.domain}/user/dashboard-analytics`,
          payload
        );

        const apiData = response?.data?.data?.[0];
        chartDataResult = [{
          period: 'This Month',
          totalCandidates: apiData?.candidates_count || 0,
          totalRecruiters: apiData?.recruiter_count || 0,
          selectedCount: apiData?.submissions_selected_count || 0,
        }];
      } else if (viewMode === 'overall') {
        // For overall view, no fromDate needed
        const response = await axios.post(
          `${Key.domain}/user/dashboard-analytics`,
          payload
        );

        const apiData = response?.data?.data?.[0];
        chartDataResult = [{
          period: 'Overall',
          totalCandidates: apiData?.candidates_count || 0,
          totalJobs: apiData?.jobs_count || 0,
          totalRecruiters: apiData?.recruiter_count || 0,
          selectedCount: apiData?.submissions_selected_count || 0,
        }];
      }

      setChartData(chartDataResult);
    } catch (error) {
      console.error("Error fetching dashboard counts:", error);
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (event) => {
    setViewMode(event.target.value);
  };

  if (loading) {
    return (
      <Card
        variant="outlined"
        sx={{
          borderRadius: 3,
          boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
            p: 2,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            borderRadius: '12px 12px 0 0',
          }}
        >
          <Skeleton variant="text" width="60%" height={32} sx={{ marginBottom: 2 }} />
          <Box display="flex" gap={2} flexWrap="wrap">
            <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 3 }} />
            <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 3 }} />
            <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 3 }} />
          </Box>
        </Box>
        <Box display="flex" justifyContent="center" alignItems="center" height={400}>
          <Box sx={{ position: 'relative' }}>
            <CircularProgress
              size={48}
              thickness={4}
              sx={{
                color: theme.palette.primary.main,
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                }
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <RichIcon
                color={theme.palette.primary.main}
                size="small"
                variant="minimal"
              >
                <BarChartIcon />
              </RichIcon>
            </Box>
          </Box>
        </Box>
      </Card>
    );
  }

  if (error) {
    return (
      <Card
        variant="outlined"
        sx={{
          borderRadius: 3,
          boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
          textAlign: "center"
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height={400}
          gap={2}
          p={4}
        >
          <RichIcon
            color={theme.palette.error.main}
            size="large"
            variant="shadow"
          >
            <InfoOutlinedIcon />
          </RichIcon>
          <Typography variant="h6" color="error" sx={{ marginBottom: 1, fontWeight: 600 }}>
            Error Loading Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {error}
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        height: "100%",
        overflow: "hidden",
        boxSizing: "border-box",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.12)}`,
        }
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
          p: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
          borderRadius: '12px 12px 0 0',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <RichIcon
              color={theme.palette.primary.main}
              size="medium"
              variant="gradient"
            >
              <DashboardIcon />
            </RichIcon>
            <Box>
              <Typography variant="h6" fontWeight={700} color="text.primary">
                {viewMode === 'daily' ? 'Daily Analysis' :
                 viewMode === 'weekly' ? 'Weekly Analysis' :
                 viewMode === 'monthly' ? 'Monthly Analysis' :
                 'Overall Analysis'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {viewMode === 'daily' ? 'Today\'s recruitment metrics and performance' :
                 viewMode === 'weekly' ? 'This week\'s recruitment metrics and performance' :
                 viewMode === 'monthly' ? 'Monthly recruitment metrics and performance indicators' :
                 'Overall recruitment performance and metrics'}
              </Typography>
            </Box>
          </Box>

          {/* Filter Dropdown */}
          <FormControl
            size="small"
            sx={{
              minWidth: 140,
              height: 40,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.9),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                fontSize: '0.875rem', // Decrease font size
                height: '40px', // Ensure equal height
                '&:hover': {
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem', // Decrease font size for label
              },
            }}
          >
            <InputLabel id="view-mode-label">Filter</InputLabel>
            <Select
              labelId="view-mode-label"
              size="small"
              value={viewMode}
              label="Filter"
              onChange={handleFilterChange}
            >
              <MenuItem value="overall">
                <Box display="flex" alignItems="center" gap={1.5}>
                  <RichIcon
                    color={theme.palette.info.main}
                    size="small"
                    variant="minimal"
                  >
                    <TrendingUpIcon />
                  </RichIcon>
                  <Typography variant="body2">Overall View</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="daily">
                <Box display="flex" alignItems="center" gap={1.5}>
                  <RichIcon
                    color={theme.palette.primary.main}
                    size="small"
                    variant="minimal"
                  >
                    <TodayIcon />
                  </RichIcon>
                  <Typography variant="body2">Daily View</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="weekly">
                <Box display="flex" alignItems="center" gap={1.5}>
                  <RichIcon
                    color={theme.palette.secondary.main}
                    size="small"
                    variant="minimal"
                  >
                    <DateRangeIcon />
                  </RichIcon>
                  <Typography variant="body2">Weekly View</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="monthly">
                <Box display="flex" alignItems="center" gap={1.5}>
                  <RichIcon
                    color={theme.palette.success.main}
                    size="small"
                    variant="minimal"
                  >
                    <CalendarMonthIcon />
                  </RichIcon>
                  <Typography variant="body2">Monthly View</Typography>
                </Box>
              </MenuItem>
             
            </Select>
          </FormControl>
        </Box>

        {/* Enhanced Summary Stats */}
        {!loading && chartData.length > 0 && (
          <Fade in={!loading}>
            <Box display="flex" gap={1} flexWrap="wrap">
              <RichChip
                icon={<PeopleIcon />}
                label="Total Candidates"
                color={theme.palette.primary.main}
                count={summaryStats.totalCandidates}
              />
              {/* <RichChip
                icon={<BriefcaseIcon />}
                label="Total Jobs"
                color={theme.palette.warning.main}
                count={summaryStats.totalJobs}
              /> */}
              <RichChip
                icon={<PersonAddIcon />}
                label="Total Recruiters"
                color={theme.palette.success.main}
                count={summaryStats.totalRecruiters}
              />
              <RichChip
                icon={<CheckCircleIcon />}
                label="Total Onboard"
                color={theme.palette.info.main}
                count={summaryStats.totalOnboard}
              />
            </Box>
          </Fade>
        )}
      </Box>

      {/* Chart Container */}
      <Box
        sx={{
          backgroundColor: alpha(theme.palette.background.default, 0.3),
          p: 3,
        }}
      >
        <ResponsiveContainer width="100%" height={420}>
          {chartData.length > 0 ? (
            <BarChart
              data={chartData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 20,
              }}
              barCategoryGap="20%"
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={alpha(theme.palette.divider, 0.3)}
                opacity={0.7}
              />
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{
                  fill: theme.palette.text.secondary,
                  fontSize: 12,
                  fontWeight: 500
                }}
                dy={10}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{
                  fill: theme.palette.text.secondary,
                  fontSize: 12,
                  fontWeight: 500
                }}
                dx={-10}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} />

              <Bar
                dataKey="totalCandidates"
                fill={theme.palette.warning.main} // Changed color to warning
                name="Total Candidates"
                radius={[4, 4, 0, 0]}
                maxBarSize={60}
              />
              <Bar
                dataKey="totalRecruiters"
                fill={theme.palette.error.main} // Changed color to error
                name="Total Recruiters"
                radius={[4, 4, 0, 0]}
                maxBarSize={60}
              />
              <Bar
                dataKey="selectedCount"
                fill={theme.palette.success.main} // Changed color to success
                name="Onboard"
                radius={[4, 4, 0, 0]}
                maxBarSize={60}
              />
            </BarChart>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
              gap={2}
            >
              <RichIcon
                color={theme.palette.text.disabled}
                size="large"
                variant="shadow"
              >
                <InfoOutlinedIcon />
              </RichIcon>
              <Typography variant="body2" color="textSecondary">
                No data available for selected range.
              </Typography>
            </Box>
          )}
        </ResponsiveContainer>
      </Box>
    </Card>
  );
};

export default Totalbarchart;